// SPDX-License-Identifier: AGPL-3.0-or-later

import type {ZoomState} from '@app/features/messaging/components/modals/media_modal/shared';
import {wasPointerDownInside} from '@app/lib/overlay/DismissGuard';
import type {AnimationPlaybackControls, MotionValue} from 'framer-motion';
import {animate, useMotionValue, useReducedMotion} from 'framer-motion';
import type React from 'react';
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {
	clampPanForScale,
	clampScale,
	DEFAULT_ZOOM_SCALE,
	getAnchoredZoomPoint,
	getAutoZoomScale,
	getCentroid,
	getDistance,
	getEffectiveMaxScale,
	getViewportPoint,
	getWheelZoomFactor,
	isDefaultTransform,
	isNotchedWheel,
	isSwipeDownIntent,
	isSwipeSideIntent,
	MAX_ZOOM_SCALE,
	MIN_ZOOM_SCALE,
	MOUSE_CLICK_MOVE_THRESHOLD,
	type PanZoomMetrics,
	type Point,
	shouldDismissAfterSwipeDown,
	swipeSideStep,
	TAP_MOVE_THRESHOLD,
	ZOOM_STATE_EPSILON,
	ZOOM_STEP,
} from './PanZoomMath';
import {useLatestRef} from './useLatestRef';

interface PointerRecord extends Point {
	pointerId: number;
}

interface PanGesture {
	mode: 'pan';
	pointerId: number;
	pointerType: string;
	startPointer: Point;
	startX: number;
	startY: number;
	moved: boolean;
	startedOnContent: boolean;
	startedOnBackdrop: boolean;
	followsPinch: boolean;
	startedAt: number;
	swipeDownActive: boolean;
	swipeSideActive: boolean;
}

interface PinchGesture {
	mode: 'pinch';
	startCentroid: Point;
	startDistance: number;
	startScale: number;
	startX: number;
	startY: number;
	moved: boolean;
}

type GestureState = PanGesture | PinchGesture | {mode: 'idle'};

export interface PanZoomTransformSnapshot {
	scale: number;
	x: number;
	y: number;
	zoomState: ZoomState;
	isDragging: boolean;
	isDefault: boolean;
	canZoomIn: boolean;
	canZoomOut: boolean;
	/** The scale at which one picture pixel is one CSS pixel; null until a picture has loaded. */
	actualSizeScale: number | null;
}

/** Safari's trackpad pinch events (not in the DOM typings). */
interface SafariGestureEvent extends UIEvent {
	scale: number;
	clientX: number;
	clientY: number;
}

export interface UsePanZoomSurfaceOptions {
	zoomState?: ZoomState;
	minScale?: number;
	maxScale?: number;
	/** 'auto' zooms to the picture's real pixels (within 2× to 4×), or 2.5× when that is unknown. */
	zoomedScale?: number | 'auto';
	/** Phones: a large picture zooms to twice its real pixels on the screen, not in CSS pixels. */
	limitZoomToScreenPixels?: boolean;
	disabled?: boolean;
	panDisabled?: boolean;
	wheelEnabled?: boolean;
	pinchEnabled?: boolean;
	doubleClickEnabled?: boolean;
	tapToToggleZoom?: boolean;
	/** A mouse click on the content toggles between fit and zoomed; a drag pans instead. */
	clickToToggleZoom?: boolean;
	/** Two quick taps on the content (touch or pen) toggle between fit and zoomed; one tap only reports onTap. */
	doubleTapToToggleZoom?: boolean;
	resetKey?: unknown;
	onZoomStateChange?: (state: ZoomState) => void;
	onTransformChange?: (snapshot: PanZoomTransformSnapshot) => void;
	/** With double tap on, a single tap is reported once the double tap window has passed. */
	onTap?: () => void;
	/** A click beside the picture; while zoomed it zooms back to fit instead. */
	onBackdropTap?: () => void;
	/** Allows a fitted touch/pen gesture that drags downward to dismiss the surrounding viewer. */
	onSwipeDownDismiss?: () => void;
	/** A fitted touch/pen swipe to the right steps back; leave unset when there is nothing before. */
	onSwipePrevious?: () => void;
	/** A fitted touch/pen swipe to the left steps on; leave unset when there is nothing after. */
	onSwipeNext?: () => void;
}

interface PanZoomSurfaceBindings {
	ref: React.RefObject<HTMLDivElement | null>;
	onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
	onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
	onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
	onPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
	onDoubleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

interface PanZoomContentBindings {
	ref: React.RefObject<HTMLDivElement | null>;
	onPointerEnter: () => void;
	onPointerLeave: () => void;
}

export interface PanZoomSurfaceController {
	x: MotionValue<number>;
	y: MotionValue<number>;
	scale: MotionValue<number>;
	zoomState: ZoomState;
	isDragging: boolean;
	isHoveringContent: boolean;
	cursor: string;
	viewportBindings: PanZoomSurfaceBindings;
	contentBindings: PanZoomContentBindings;
	/** Changes for each item and each return to the page: key the content with it so its layer is made anew. */
	contentKey: string;
	zoomTo: (state: ZoomState, origin?: Point) => void;
	zoomIn: (origin?: Point) => void;
	zoomOut: (origin?: Point) => void;
	toggleActualSize: () => void;
	panBy: (dx: number, dy: number) => void;
	reset: () => void;
	getSnapshot: () => PanZoomTransformSnapshot;
}

const ZOOM_TRANSITION = {
	type: 'spring' as const,
	stiffness: 300,
	damping: 30,
	mass: 1,
};
const NON_PASSIVE_EVENT_LISTENER_OPTIONS: AddEventListenerOptions = {passive: false};
const DOUBLE_TAP_WINDOW_MS = 320;
const DOUBLE_TAP_DISTANCE = 32;
const SWIPE_SIDE_EXIT_MS = 140;
/** Wheel events further apart than this start a new wheel gesture (and a fresh mouse/trackpad guess). */
const WHEEL_GESTURE_GAP_MS = 240;
/** A released pan keeps gliding when it was still moving at least this fast (px/s). */
const INERTIA_MIN_VELOCITY = 150;
/** How far the picture follows the finger when there is nothing to step to that way. */
const SWIPE_SIDE_EDGE_RESISTANCE = 0.25;
const MEASURED_BOX_SELECTOR = '[data-pan-zoom-measured-box]';
const MEASURED_ELEMENT_SELECTOR = 'img, video, canvas, svg';

function measureActualSizeScale(media: Element | null): number | null {
	if (!(media instanceof HTMLImageElement)) return null;
	if (media.naturalWidth <= 0 || media.offsetWidth <= 0) return null;
	return media.naturalWidth / media.offsetWidth;
}

function toZoomState(scale: number, minScale: number): ZoomState {
	return scale <= minScale + ZOOM_STATE_EPSILON ? 'fit' : 'zoomed';
}

function mix(start: number, end: number, progress: number): number {
	return start + (end - start) * progress;
}

function getPointerRecords(records: Map<number, PointerRecord>): Array<PointerRecord> {
	return Array.from(records.values());
}

/**
 * Counts the page's returns on screen. iOS can drop the picture's layer while the viewer is open (in the
 * background, or short of memory) and then draws a flat light box in its place until the layer is made
 * again, which used to take closing the viewer.
 */
function usePageReturnCount(): number {
	const [count, setCount] = useState(0);
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') setCount((value) => value + 1);
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
	}, []);
	return count;
}

function getMediaCandidates(content: HTMLElement): Array<Element> {
	const measuredBox = content.querySelector<HTMLElement>(MEASURED_BOX_SELECTOR) ?? content;
	return Array.from(measuredBox.querySelectorAll(MEASURED_ELEMENT_SELECTOR));
}

function findMediaElement(content: HTMLElement): Element | null {
	let widest: Element | null = null;
	let widestArea = 0;
	for (const candidate of getMediaCandidates(content)) {
		const rect = candidate.getBoundingClientRect();
		const area = rect.width * rect.height;
		if (area > widestArea) {
			widestArea = area;
			widest = candidate;
		}
	}
	return widest;
}

export function usePanZoomSurface(options: UsePanZoomSurfaceOptions): PanZoomSurfaceController {
	const {
		zoomState: controlledZoomState,
		minScale = MIN_ZOOM_SCALE,
		maxScale = MAX_ZOOM_SCALE,
		zoomedScale = DEFAULT_ZOOM_SCALE,
		limitZoomToScreenPixels = false,
		disabled = false,
		panDisabled = false,
		wheelEnabled = true,
		pinchEnabled = true,
		doubleClickEnabled = true,
		tapToToggleZoom = false,
		clickToToggleZoom = false,
		doubleTapToToggleZoom = false,
		resetKey,
		onZoomStateChange,
		onTransformChange,
		onTap,
		onBackdropTap,
		onSwipeDownDismiss,
		onSwipePrevious,
		onSwipeNext,
	} = options;
	const viewportRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const returnCount = usePageReturnCount();
	// iOS can also lose the picture's layer while it is zoomed far in, and keeps the light box after it
	// zooms back out; a fresh layer each time it settles back at fit brings the picture back.
	const [fitReturnCount, setFitReturnCount] = useState(0);
	const zoomedSinceFitRef = useRef(false);
	const contentKey = `${String(resetKey)}:${returnCount}:${fitReturnCount}`;
	const x = useMotionValue(0);
	const y = useMotionValue(0);
	const scale = useMotionValue(minScale);
	const requestedTransformRef = useRef({scale: minScale, x: 0, y: 0});
	const prefersReducedMotion = useReducedMotion();
	const [internalZoomState, setInternalZoomState] = useState<ZoomState>(controlledZoomState ?? 'fit');
	const [isDragging, setIsDragging] = useState(false);
	const [isHoveringContent, setIsHoveringContent] = useState(false);
	const pointerRecordsRef = useRef(new Map<number, PointerRecord>());
	const gestureRef = useRef<GestureState>({mode: 'idle'});
	const metricsRef = useRef<PanZoomMetrics | null>(null);
	const viewportRectRef = useRef<DOMRectReadOnly | null>(null);
	const animationControlsRef = useRef<Array<AnimationPlaybackControls>>([]);
	const metricsFrameRef = useRef<number | null>(null);
	const isDraggingRef = useRef(false);
	const lastCommittedZoomStateRef = useRef<ZoomState>(controlledZoomState ?? 'fit');
	const controlledZoomStateRef = useLatestRef(controlledZoomState);
	const onZoomStateChangeRef = useLatestRef(onZoomStateChange);
	const onTransformChangeRef = useLatestRef(onTransformChange);
	const onTapRef = useLatestRef(onTap);
	const onBackdropTapRef = useLatestRef(onBackdropTap);
	const onSwipeDownDismissRef = useLatestRef(onSwipeDownDismiss);
	const onSwipePreviousRef = useLatestRef(onSwipePrevious);
	const onSwipeNextRef = useLatestRef(onSwipeNext);
	const lastTapRef = useRef<{at: number; x: number; y: number} | null>(null);
	const singleTapTimerRef = useRef<number | null>(null);
	const wheelGestureRef = useRef<{notched: boolean; lastAt: number} | null>(null);
	const actualSizeScaleRef = useRef<number | null>(null);
	const isGlidingRef = useRef(false);
	const activeZoomState = controlledZoomState ?? internalZoomState;
	const minScaleRef = useLatestRef(minScale);
	const maxScaleRef = useLatestRef(maxScale);
	const zoomedScaleRef = useLatestRef(zoomedScale);
	const limitZoomToScreenPixelsRef = useLatestRef(limitZoomToScreenPixels);
	const disabledRef = useLatestRef(disabled);
	const panDisabledRef = useLatestRef(panDisabled);
	const stopAnimations = useCallback(() => {
		if (animationControlsRef.current.length === 0) return;
		for (const controls of animationControlsRef.current) {
			controls.stop();
		}
		animationControlsRef.current = [];
		requestedTransformRef.current = {scale: scale.get(), x: x.get(), y: y.get()};
	}, [scale, x, y]);
	const measureMetrics = useCallback((): PanZoomMetrics | null => {
		const viewport = viewportRef.current;
		const content = contentRef.current;
		if (!viewport || !content) return null;
		const viewportRect = viewport.getBoundingClientRect();
		viewportRectRef.current = viewportRect;
		const activeScale = scale.get();
		const inverseScale = activeScale > 0 ? 1 / activeScale : 1;
		const media = findMediaElement(content);
		actualSizeScaleRef.current = measureActualSizeScale(media);
		const mediaRect = media?.getBoundingClientRect();
		const hasMediaRect = mediaRect != null && mediaRect.width > 0 && mediaRect.height > 0;
		const metrics = {
			viewportWidth: viewportRect.width,
			viewportHeight: viewportRect.height,
			contentWidth: hasMediaRect ? mediaRect.width * inverseScale : content.offsetWidth || viewportRect.width,
			contentHeight: hasMediaRect ? mediaRect.height * inverseScale : content.offsetHeight || viewportRect.height,
		};
		metricsRef.current = metrics;
		return metrics;
	}, [scale]);
	const getMetrics = useCallback((): PanZoomMetrics | null => metricsRef.current ?? measureMetrics(), [measureMetrics]);
	const scheduleMetricsRefresh = useCallback(() => {
		if (typeof window === 'undefined' || metricsFrameRef.current != null) return;
		metricsFrameRef.current = window.requestAnimationFrame(() => {
			metricsFrameRef.current = null;
			measureMetrics();
		});
	}, [measureMetrics]);
	const commitZoomState = useCallback(
		(nextZoomState: ZoomState, emit = true) => {
			if (lastCommittedZoomStateRef.current === nextZoomState) return;
			lastCommittedZoomStateRef.current = nextZoomState;
			if (controlledZoomStateRef.current === undefined) {
				setInternalZoomState(nextZoomState);
			}
			if (emit) {
				onZoomStateChangeRef.current?.(nextZoomState);
			}
		},
		[controlledZoomStateRef, onZoomStateChangeRef],
	);
	const getMaxScale = useCallback(
		() =>
			getEffectiveMaxScale(
				maxScaleRef.current,
				actualSizeScaleRef.current,
				limitZoomToScreenPixelsRef.current && typeof window !== 'undefined' ? window.devicePixelRatio : 1,
			),
		[limitZoomToScreenPixelsRef, maxScaleRef],
	);
	const canStepScale = useCallback(
		(fromScale: number, factor: number) =>
			clampScale(fromScale * factor, minScaleRef.current, getMaxScale()) !== fromScale,
		[getMaxScale, minScaleRef],
	);
	const getSnapshot = useCallback((): PanZoomTransformSnapshot => {
		const requested = requestedTransformRef.current;
		return {
			scale: requested.scale,
			x: requested.x,
			y: requested.y,
			zoomState: lastCommittedZoomStateRef.current,
			isDragging: gestureRef.current.mode !== 'idle',
			isDefault: isDefaultTransform({
				scale: requested.scale,
				x: requested.x,
				y: requested.y,
				minScale: minScaleRef.current,
			}),
			canZoomIn: canStepScale(requested.scale, ZOOM_STEP),
			canZoomOut: canStepScale(requested.scale, 1 / ZOOM_STEP),
			actualSizeScale: actualSizeScaleRef.current,
		};
	}, [canStepScale, minScaleRef]);
	const commitRequestedTransform = useCallback(
		(next: {scale: number; x: number; y: number}) => {
			requestedTransformRef.current = next;
			if (next.scale > minScaleRef.current + ZOOM_STATE_EPSILON) zoomedSinceFitRef.current = true;
			commitZoomState(toZoomState(next.scale, minScaleRef.current));
			onTransformChangeRef.current?.(getSnapshot());
		},
		[commitZoomState, getSnapshot, minScaleRef, onTransformChangeRef],
	);
	const clampPanToMetrics = useCallback(
		(metrics: PanZoomMetrics | null) => {
			if (!metrics) return;
			const currentX = x.get();
			const currentY = y.get();
			const clamped = clampPanForScale({x: currentX, y: currentY}, scale.get(), metrics);
			if (clamped.x === currentX && clamped.y === currentY) return;
			x.set(clamped.x);
			y.set(clamped.y);
			commitRequestedTransform({scale: requestedTransformRef.current.scale, x: clamped.x, y: clamped.y});
		},
		[commitRequestedTransform, scale, x, y],
	);
	/** Called once the picture has come to rest at fit. */
	const rebuildLayerAfterZoom = useCallback(() => {
		if (!zoomedSinceFitRef.current || scale.get() > minScaleRef.current + ZOOM_STATE_EPSILON) return;
		zoomedSinceFitRef.current = false;
		setFitReturnCount((value) => value + 1);
	}, [minScaleRef, scale]);
	const updateDragging = useCallback((nextIsDragging: boolean) => {
		if (isDraggingRef.current === nextIsDragging) return;
		isDraggingRef.current = nextIsDragging;
		setIsDragging(nextIsDragging);
	}, []);
	useLayoutEffect(() => {
		measureMetrics();
		const viewport = viewportRef.current;
		const content = contentRef.current;
		if (!viewport || !content) return;
		const ownerWindow = viewport.ownerDocument.defaultView;
		const handleResize = () => {
			clampPanToMetrics(measureMetrics());
		};
		// The picture's real size is only known once it loads; report it so the zoom level can show it.
		const handleMediaLoad = () => {
			measureMetrics();
			onTransformChangeRef.current?.(getSnapshot());
		};
		const handleSettledTransform = (event: Event) => {
			if ((event as TransitionEvent).propertyName !== 'transform') return;
			if (event.target === content) return;
			clampPanToMetrics(measureMetrics());
		};
		ownerWindow?.addEventListener('resize', handleResize);
		content.addEventListener('transitionend', handleSettledTransform);
		content.addEventListener('transitioncancel', handleSettledTransform);
		content.addEventListener('load', handleMediaLoad, true);
		const ResizeObserverCtor = ownerWindow?.ResizeObserver;
		if (!ResizeObserverCtor) {
			return () => {
				ownerWindow?.removeEventListener('resize', handleResize);
				content.removeEventListener('transitionend', handleSettledTransform);
				content.removeEventListener('transitioncancel', handleSettledTransform);
				content.removeEventListener('load', handleMediaLoad, true);
			};
		}
		const observer = new ResizeObserverCtor(handleResize);
		observer.observe(viewport);
		observer.observe(content);
		for (const candidate of getMediaCandidates(content)) {
			observer.observe(candidate);
		}
		return () => {
			observer.disconnect();
			ownerWindow?.removeEventListener('resize', handleResize);
			content.removeEventListener('transitionend', handleSettledTransform);
			content.removeEventListener('transitioncancel', handleSettledTransform);
			content.removeEventListener('load', handleMediaLoad, true);
		};
	}, [clampPanToMetrics, getSnapshot, measureMetrics, onTransformChangeRef, contentKey]);
	const getZoomedScale = useCallback(() => {
		const preferred = zoomedScaleRef.current;
		const target = preferred === 'auto' ? getAutoZoomScale(actualSizeScaleRef.current, DEFAULT_ZOOM_SCALE) : preferred;
		return clampScale(target, minScaleRef.current, getMaxScale());
	}, [getMaxScale, minScaleRef, zoomedScaleRef]);
	/** A pan still gliding after release has not been committed yet; stop it where it is first. */
	const settleGlide = useCallback(() => {
		if (!isGlidingRef.current) return;
		isGlidingRef.current = false;
		stopAnimations();
	}, [stopAnimations]);
	const setTransform = useCallback(
		(next: {scale: number; x: number; y: number}, animated: boolean) => {
			isGlidingRef.current = false;
			stopAnimations();
			commitRequestedTransform(next);
			if (animated && !prefersReducedMotion) {
				const start = {
					scale: scale.get(),
					x: x.get(),
					y: y.get(),
				};
				const controls = animate(0, 1, {
					...ZOOM_TRANSITION,
					onUpdate: (progress) => {
						x.set(mix(start.x, next.x, progress));
						y.set(mix(start.y, next.y, progress));
						scale.set(mix(start.scale, next.scale, progress));
					},
					onComplete: () => {
						x.set(next.x);
						y.set(next.y);
						scale.set(next.scale);
						animationControlsRef.current = [];
						rebuildLayerAfterZoom();
					},
				});
				animationControlsRef.current = [controls];
			} else {
				x.set(next.x);
				y.set(next.y);
				scale.set(next.scale);
				rebuildLayerAfterZoom();
			}
		},
		[commitRequestedTransform, prefersReducedMotion, rebuildLayerAfterZoom, scale, stopAnimations, x, y],
	);
	const zoomTo = useCallback(
		(nextZoomState: ZoomState, origin?: Point) => {
			settleGlide();
			const metrics = measureMetrics();
			if (!metrics) return;
			const nextScale = nextZoomState === 'fit' ? minScaleRef.current : getZoomedScale();
			const requested = requestedTransformRef.current;
			const nextPoint =
				nextZoomState === 'fit'
					? {x: 0, y: 0}
					: getAnchoredZoomPoint({
							origin: origin ?? {x: 0, y: 0},
							current: {x: requested.x, y: requested.y},
							currentScale: requested.scale,
							nextScale,
							metrics,
						});
			setTransform({scale: nextScale, x: nextPoint.x, y: nextPoint.y}, true);
		},
		[getZoomedScale, measureMetrics, minScaleRef, setTransform, settleGlide],
	);
	const zoomToScale = useCallback(
		(targetScale: number, origin?: Point, animated = true) => {
			settleGlide();
			const metrics = measureMetrics();
			if (!metrics) return;
			const requested = requestedTransformRef.current;
			const currentScale = requested.scale;
			const nextScale = clampScale(targetScale, minScaleRef.current, getMaxScale());
			if (nextScale === currentScale) return;
			const nextPoint = getAnchoredZoomPoint({
				origin: origin ?? {x: 0, y: 0},
				current: {x: requested.x, y: requested.y},
				currentScale,
				nextScale,
				metrics,
			});
			setTransform({scale: nextScale, x: nextPoint.x, y: nextPoint.y}, animated);
		},
		[getMaxScale, measureMetrics, minScaleRef, setTransform, settleGlide],
	);
	const zoomBy = useCallback(
		(factor: number, origin?: Point, animated = true) => {
			settleGlide();
			zoomToScale(requestedTransformRef.current.scale * factor, origin, animated);
		},
		[settleGlide, zoomToScale],
	);
	const zoomIn = useCallback((origin?: Point) => zoomBy(ZOOM_STEP, origin), [zoomBy]);
	const zoomOut = useCallback((origin?: Point) => zoomBy(1 / ZOOM_STEP, origin), [zoomBy]);
	/** Fitted: show the picture at its real pixels (or the usual zoom when it already fits). Otherwise back to fit. */
	const toggleActualSize = useCallback(() => {
		settleGlide();
		measureMetrics();
		const fitted = requestedTransformRef.current.scale <= minScaleRef.current + ZOOM_STATE_EPSILON;
		if (!fitted) {
			zoomTo('fit');
			return;
		}
		const actual = actualSizeScaleRef.current;
		if (actual != null && actual > minScaleRef.current + ZOOM_STATE_EPSILON) {
			zoomToScale(actual);
			return;
		}
		zoomTo('zoomed');
	}, [measureMetrics, minScaleRef, settleGlide, zoomTo, zoomToScale]);
	const panBy = useCallback(
		(dx: number, dy: number) => {
			settleGlide();
			const metrics = measureMetrics();
			if (!metrics) return;
			const requested = requestedTransformRef.current;
			if (requested.scale <= minScaleRef.current + ZOOM_STATE_EPSILON) return;
			const nextPoint = clampPanForScale({x: requested.x + dx, y: requested.y + dy}, requested.scale, metrics);
			if (nextPoint.x === requested.x && nextPoint.y === requested.y) return;
			setTransform({scale: requested.scale, x: nextPoint.x, y: nextPoint.y}, true);
		},
		[measureMetrics, minScaleRef, setTransform, settleGlide],
	);
	const reset = useCallback(() => {
		setTransform({scale: minScaleRef.current, x: 0, y: 0}, true);
	}, [minScaleRef, setTransform]);
	/** After a pan, let the picture glide on with the finger's speed and settle against the edges. */
	const glideAfterPan = useCallback(() => {
		if (prefersReducedMotion) return;
		const metrics = getMetrics();
		if (!metrics) return;
		const vx = x.getVelocity();
		const vy = y.getVelocity();
		if (Math.hypot(vx, vy) < INERTIA_MIN_VELOCITY) return;
		const currentScale = requestedTransformRef.current.scale;
		const maxX = Math.max(0, (metrics.contentWidth * currentScale - metrics.viewportWidth) / 2);
		const maxY = Math.max(0, (metrics.contentHeight * currentScale - metrics.viewportHeight) / 2);
		stopAnimations();
		isGlidingRef.current = true;
		let running = 2;
		const settle = () => {
			running -= 1;
			if (running > 0 || !isGlidingRef.current) return;
			isGlidingRef.current = false;
			animationControlsRef.current = [];
			commitRequestedTransform({scale: currentScale, x: x.get(), y: y.get()});
		};
		const glide = {type: 'inertia' as const, power: 0.35, timeConstant: 260, bounceStiffness: 500, bounceDamping: 40};
		animationControlsRef.current = [
			animate(x, x.get(), {...glide, velocity: maxX > 0 ? vx : 0, min: -maxX, max: maxX, onComplete: settle}),
			animate(y, y.get(), {...glide, velocity: maxY > 0 ? vy : 0, min: -maxY, max: maxY, onComplete: settle}),
		];
	}, [commitRequestedTransform, getMetrics, prefersReducedMotion, stopAnimations, x, y]);
	useEffect(() => {
		if (controlledZoomState === undefined) return;
		if (lastCommittedZoomStateRef.current === controlledZoomState) return;
		commitZoomState(controlledZoomState, false);
		zoomTo(controlledZoomState);
	}, [commitZoomState, controlledZoomState, zoomTo]);
	useEffect(() => {
		measureMetrics();
		reset();
	}, [measureMetrics, reset, resetKey]);
	useEffect(() => {
		return () => stopAnimations();
	}, [stopAnimations]);
	const clearSingleTapTimer = useCallback(() => {
		if (singleTapTimerRef.current == null) return;
		window.clearTimeout(singleTapTimerRef.current);
		singleTapTimerRef.current = null;
	}, []);
	useEffect(() => clearSingleTapTimer, [clearSingleTapTimer]);
	useEffect(() => {
		return () => {
			if (typeof window === 'undefined') return;
			if (metricsFrameRef.current != null) {
				window.cancelAnimationFrame(metricsFrameRef.current);
			}
		};
	}, []);
	const updatePointerRecord = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		pointerRecordsRef.current.set(event.pointerId, {
			pointerId: event.pointerId,
			x: event.clientX,
			y: event.clientY,
		});
	}, []);
	const getViewportRelativePoint = useCallback(
		(clientX: number, clientY: number): Point | null => {
			let rect = viewportRectRef.current;
			if (!rect) {
				measureMetrics();
				rect = viewportRectRef.current;
			}
			if (!rect) return null;
			return getViewportPoint(clientX, clientY, rect);
		},
		[measureMetrics],
	);
	const handleWheel = useCallback(
		(event: WheelEvent) => {
			if (disabledRef.current || !wheelEnabled) return;
			// A trackpad pinch arrives as ctrl+wheel; never let it zoom the whole page instead.
			const isZoomGesture = event.ctrlKey || event.metaKey;
			if (isZoomGesture) event.preventDefault();
			const metrics = getMetrics();
			if (!metrics) return;
			// Decide mouse or trackpad once per gesture so a stray delta cannot flip a pan into a zoom.
			let wheelGesture = wheelGestureRef.current;
			if (!wheelGesture || event.timeStamp - wheelGesture.lastAt > WHEEL_GESTURE_GAP_MS) {
				wheelGesture = {
					notched: isNotchedWheel(event as WheelEvent & {wheelDeltaY?: number}),
					lastAt: event.timeStamp,
				};
				wheelGestureRef.current = wheelGesture;
			}
			wheelGesture.lastAt = event.timeStamp;
			const requested = requestedTransformRef.current;
			const fitted = requested.scale <= minScaleRef.current + ZOOM_STATE_EPSILON;
			// Pinch and ctrl+wheel zoom; a mouse wheel zooms too. A trackpad's two-finger scroll pans.
			if (isZoomGesture || (wheelGesture.notched && !event.shiftKey && event.deltaY !== 0)) {
				event.preventDefault();
				const factor = getWheelZoomFactor(event, wheelGesture.notched, metrics.viewportHeight);
				if (factor === 1) return;
				const origin = getViewportRelativePoint(event.clientX, event.clientY) ?? {x: 0, y: 0};
				zoomBy(factor, origin, wheelGesture.notched);
				scheduleMetricsRefresh();
				return;
			}
			if (panDisabledRef.current || fitted) return;
			event.preventDefault();
			settleGlide();
			stopAnimations();
			const current = requestedTransformRef.current;
			// Shift turns a mouse wheel sideways.
			const deltaX = wheelGesture.notched && event.shiftKey && event.deltaX === 0 ? event.deltaY : event.deltaX;
			const deltaY = wheelGesture.notched && event.shiftKey ? 0 : event.deltaY;
			const pannedPoint = clampPanForScale({x: current.x - deltaX, y: current.y - deltaY}, current.scale, metrics);
			x.set(pannedPoint.x);
			y.set(pannedPoint.y);
			commitRequestedTransform({scale: current.scale, x: pannedPoint.x, y: pannedPoint.y});
			scheduleMetricsRefresh();
		},
		[
			commitRequestedTransform,
			disabledRef,
			getMetrics,
			getViewportRelativePoint,
			minScaleRef,
			panDisabledRef,
			scheduleMetricsRefresh,
			settleGlide,
			stopAnimations,
			wheelEnabled,
			x,
			y,
			zoomBy,
		],
	);
	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;
		viewport.addEventListener('wheel', handleWheel, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		return () => {
			viewport.removeEventListener('wheel', handleWheel, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		};
	}, [handleWheel]);
	// Safari reports a trackpad pinch as gesture events rather than ctrl+wheel. iOS sends them alongside
	// the touch pointers we already follow, so they are ignored while fingers are down.
	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport || !pinchEnabled) return;
		let startScale = minScaleRef.current;
		const isPointerPinch = () => pointerRecordsRef.current.size > 0;
		const handleGestureStart = (event: Event) => {
			if (disabledRef.current || isPointerPinch()) return;
			event.preventDefault();
			settleGlide();
			startScale = requestedTransformRef.current.scale;
		};
		const handleGestureChange = (event: Event) => {
			if (disabledRef.current || isPointerPinch()) return;
			event.preventDefault();
			const gesture = event as SafariGestureEvent;
			const origin = getViewportRelativePoint(gesture.clientX, gesture.clientY) ?? {x: 0, y: 0};
			zoomToScale(startScale * gesture.scale, origin, false);
		};
		const handleGestureEnd = (event: Event) => {
			if (!isPointerPinch()) event.preventDefault();
		};
		viewport.addEventListener('gesturestart', handleGestureStart, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		viewport.addEventListener('gesturechange', handleGestureChange, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		viewport.addEventListener('gestureend', handleGestureEnd, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		return () => {
			viewport.removeEventListener('gesturestart', handleGestureStart, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
			viewport.removeEventListener('gesturechange', handleGestureChange, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
			viewport.removeEventListener('gestureend', handleGestureEnd, NON_PASSIVE_EVENT_LISTENER_OPTIONS);
		};
	}, [disabledRef, getViewportRelativePoint, minScaleRef, pinchEnabled, settleGlide, zoomToScale]);
	const handlePointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (event.button !== 0 && event.pointerType === 'mouse') return;
			measureMetrics();
			updatePointerRecord(event);
			event.currentTarget.setPointerCapture?.(event.pointerId);
			const target = event.target as Node | null;
			const startedOnContent = Boolean(target && contentRef.current?.contains(target));
			const startedOnBackdrop = event.target === event.currentTarget;
			const pointer: Point = {x: event.clientX, y: event.clientY};
			if (!disabledRef.current) {
				isGlidingRef.current = false;
				stopAnimations();
			}
			const currentScale = scale.get();
			if (pointerRecordsRef.current.size === 1) {
				gestureRef.current = {
					mode: 'pan',
					pointerId: event.pointerId,
					pointerType: event.pointerType,
					startPointer: pointer,
					startX: x.get(),
					startY: y.get(),
					moved: false,
					startedOnContent,
					startedOnBackdrop,
					followsPinch: false,
					startedAt: event.timeStamp,
					swipeDownActive: false,
					swipeSideActive: false,
				};
				updateDragging(
					!disabledRef.current && !panDisabledRef.current && currentScale > minScaleRef.current + ZOOM_STATE_EPSILON,
				);
				return;
			}
			if (!pinchEnabled || disabledRef.current || pointerRecordsRef.current.size !== 2) return;
			if (
				gestureRef.current.mode === 'pan' &&
				(gestureRef.current.swipeDownActive || gestureRef.current.swipeSideActive)
			) {
				x.set(0);
				y.set(0);
			}
			const records = getPointerRecords(pointerRecordsRef.current);
			const viewportRect = viewportRectRef.current;
			if (!viewportRect || records.length !== 2) return;
			const first = getViewportPoint(records[0].x, records[0].y, viewportRect);
			const second = getViewportPoint(records[1].x, records[1].y, viewportRect);
			gestureRef.current = {
				mode: 'pinch',
				startCentroid: getCentroid(first, second),
				startDistance: Math.max(1, getDistance(first, second)),
				startScale: currentScale,
				startX: x.get(),
				startY: y.get(),
				moved: false,
			};
			updateDragging(true);
		},
		[
			disabledRef,
			measureMetrics,
			minScaleRef,
			panDisabledRef,
			pinchEnabled,
			scale,
			stopAnimations,
			updateDragging,
			updatePointerRecord,
			x,
			y,
		],
	);
	const handlePointerMove = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (!pointerRecordsRef.current.has(event.pointerId)) return;
			updatePointerRecord(event);
			const gesture = gestureRef.current;
			if (gesture.mode === 'idle' || disabledRef.current) return;
			const metrics = getMetrics();
			if (!metrics) return;
			if (gesture.mode === 'pan') {
				const dx = event.clientX - gesture.startPointer.x;
				const dy = event.clientY - gesture.startPointer.y;
				const moveThreshold = gesture.pointerType === 'mouse' ? MOUSE_CLICK_MOVE_THRESHOLD : TAP_MOVE_THRESHOLD;
				gesture.moved = gesture.moved || Math.hypot(dx, dy) >= moveThreshold;
				const fitted = scale.get() <= minScaleRef.current + ZOOM_STATE_EPSILON;
				const canSwipeSide =
					(onSwipePreviousRef.current != null || onSwipeNextRef.current != null) &&
					event.pointerType !== 'mouse' &&
					fitted &&
					!gesture.swipeDownActive;
				if (canSwipeSide && (gesture.swipeSideActive || isSwipeSideIntent(dx, dy))) {
					gesture.swipeSideActive = true;
					// Nothing to step to that way: the picture only gives a little.
					const open = dx < 0 ? onSwipeNextRef.current != null : onSwipePreviousRef.current != null;
					x.set(open ? dx : dx * SWIPE_SIDE_EDGE_RESISTANCE);
					y.set(0);
					updateDragging(true);
					return;
				}
				const canSwipeDownToDismiss =
					onSwipeDownDismissRef.current != null &&
					event.pointerType !== 'mouse' &&
					gesture.startedOnContent &&
					fitted &&
					!gesture.swipeSideActive;
				if (canSwipeDownToDismiss && (gesture.swipeDownActive || isSwipeDownIntent(dx, dy))) {
					gesture.swipeDownActive = true;
					x.set(dx * 0.12);
					y.set(Math.max(0, dy));
					updateDragging(true);
					return;
				}
				if (panDisabledRef.current || scale.get() <= minScaleRef.current + ZOOM_STATE_EPSILON) return;
				const nextPoint = clampPanForScale(
					{
						x: gesture.startX + dx,
						y: gesture.startY + dy,
					},
					scale.get(),
					metrics,
				);
				x.set(nextPoint.x);
				y.set(nextPoint.y);
				commitRequestedTransform({scale: requestedTransformRef.current.scale, x: nextPoint.x, y: nextPoint.y});
				updateDragging(true);
				return;
			}
			if (!pinchEnabled) return;
			const records = getPointerRecords(pointerRecordsRef.current);
			if (records.length < 2) return;
			const viewportRect = viewportRectRef.current;
			if (!viewportRect) return;
			const first = getViewportPoint(records[0].x, records[0].y, viewportRect);
			const second = getViewportPoint(records[1].x, records[1].y, viewportRect);
			const centroid = getCentroid(first, second);
			const distance = Math.max(1, getDistance(first, second));
			const nextScale = Math.min(
				getMaxScale(),
				Math.max(minScaleRef.current, gesture.startScale * (distance / gesture.startDistance)),
			);
			const localPoint = {
				x: (gesture.startCentroid.x - gesture.startX) / gesture.startScale,
				y: (gesture.startCentroid.y - gesture.startY) / gesture.startScale,
			};
			const nextPoint = clampPanForScale(
				{
					x: centroid.x - localPoint.x * nextScale,
					y: centroid.y - localPoint.y * nextScale,
				},
				nextScale,
				metrics,
			);
			gesture.moved = true;
			scale.set(nextScale);
			x.set(nextPoint.x);
			y.set(nextPoint.y);
			commitRequestedTransform({scale: nextScale, x: nextPoint.x, y: nextPoint.y});
		},
		[
			commitRequestedTransform,
			disabledRef,
			getMaxScale,
			getMetrics,
			minScaleRef,
			onSwipeDownDismissRef,
			onSwipeNextRef,
			onSwipePreviousRef,
			panDisabledRef,
			pinchEnabled,
			scale,
			updateDragging,
			updatePointerRecord,
			x,
			y,
		],
	);
	const finishPointer = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const gesture = gestureRef.current;
			pointerRecordsRef.current.delete(event.pointerId);
			event.currentTarget.releasePointerCapture?.(event.pointerId);
			if (gesture.mode === 'pan' && gesture.pointerId === event.pointerId) {
				const dx = event.clientX - gesture.startPointer.x;
				const dy = event.clientY - gesture.startPointer.y;
				const moveThreshold = gesture.pointerType === 'mouse' ? MOUSE_CLICK_MOVE_THRESHOLD : TAP_MOVE_THRESHOLD;
				const moved = gesture.moved || Math.hypot(dx, dy) >= moveThreshold;
				const wasSwipeDownActive = gesture.swipeDownActive;
				const wasSwipeSideActive = gesture.swipeSideActive;
				updateDragging(false);
				gestureRef.current = {mode: 'idle'};
				if (wasSwipeSideActive) {
					const viewportWidth = viewportRectRef.current?.width ?? 0;
					const step = swipeSideStep({dx, dy, durationMs: event.timeStamp - gesture.startedAt, viewportWidth});
					const go = step === 1 ? onSwipeNextRef.current : step === -1 ? onSwipePreviousRef.current : undefined;
					if (!go) {
						setTransform({scale: minScaleRef.current, x: 0, y: 0}, true);
						return;
					}
					if (prefersReducedMotion) {
						go();
						return;
					}
					// The picture leaves the way the finger went; the next one starts on the other side and
					// the new item's resetKey springs it into the centre.
					const exitX = (step === 1 ? -1 : 1) * Math.max(viewportWidth, 1);
					stopAnimations();
					animationControlsRef.current = [
						animate(x, exitX, {
							duration: SWIPE_SIDE_EXIT_MS / 1000,
							ease: 'easeOut',
							onComplete: () => {
								animationControlsRef.current = [];
								x.set(-exitX);
								go();
								// Should the step not land (no new item), bring this one back rather than leave it off screen.
								window.setTimeout(() => {
									if (x.get() === -exitX && animationControlsRef.current.length === 0) {
										setTransform({scale: minScaleRef.current, x: 0, y: 0}, true);
									}
								}, 400);
							},
						}),
					];
					return;
				}
				if (wasSwipeDownActive) {
					const viewportHeight = viewportRectRef.current?.height ?? 0;
					if (
						shouldDismissAfterSwipeDown({
							dx,
							dy,
							durationMs: event.timeStamp - gesture.startedAt,
							viewportHeight,
						})
					) {
						onSwipeDownDismissRef.current?.();
					} else {
						setTransform({scale: minScaleRef.current, x: 0, y: 0}, true);
					}
					return;
				}
				const zoomed = lastCommittedZoomStateRef.current === 'zoomed';
				if (moved && zoomed && !panDisabledRef.current && pointerRecordsRef.current.size === 0) {
					glideAfterPan();
				}
				if (!moved && !gesture.followsPinch) {
					if (gesture.startedOnBackdrop && !wasPointerDownInside(contentRef.current)) {
						// Beside a zoomed picture, a click brings it back to fit rather than closing the viewer.
						if (zoomed && onBackdropTapRef.current && !disabledRef.current) {
							zoomTo('fit');
							return;
						}
						onBackdropTapRef.current?.();
						return;
					}
					if (gesture.startedOnContent) {
						const origin = () => getViewportRelativePoint(event.clientX, event.clientY) ?? {x: 0, y: 0};
						const toggleZoom = () => zoomTo(lastCommittedZoomStateRef.current === 'fit' ? 'zoomed' : 'fit', origin());
						if (clickToToggleZoom && event.pointerType === 'mouse' && !disabledRef.current) {
							toggleZoom();
						} else if (tapToToggleZoom && !disabledRef.current) {
							onTapRef.current?.();
							toggleZoom();
						} else if (doubleTapToToggleZoom && !disabledRef.current && event.pointerType !== 'mouse') {
							// Touch and pen count two quick taps; a lone tap is only reported once no second one came.
							const now = performance.now();
							const last = lastTapRef.current;
							const isDouble =
								last != null &&
								now - last.at <= DOUBLE_TAP_WINDOW_MS &&
								Math.hypot(event.clientX - last.x, event.clientY - last.y) <= DOUBLE_TAP_DISTANCE;
							lastTapRef.current = isDouble ? null : {at: now, x: event.clientX, y: event.clientY};
							clearSingleTapTimer();
							if (isDouble) {
								toggleZoom();
							} else if (onTapRef.current) {
								singleTapTimerRef.current = window.setTimeout(() => {
									singleTapTimerRef.current = null;
									onTapRef.current?.();
								}, DOUBLE_TAP_WINDOW_MS);
							}
						} else {
							onTapRef.current?.();
						}
					}
				}
			}
			if (pointerRecordsRef.current.size === 1) {
				const [remaining] = getPointerRecords(pointerRecordsRef.current);
				gestureRef.current = {
					mode: 'pan',
					pointerId: remaining.pointerId,
					pointerType: event.pointerType,
					startPointer: {x: remaining.x, y: remaining.y},
					startX: x.get(),
					startY: y.get(),
					moved: false,
					startedOnContent: true,
					startedOnBackdrop: false,
					followsPinch: true,
					startedAt: event.timeStamp,
					swipeDownActive: false,
					swipeSideActive: false,
				};
				updateDragging(scale.get() > minScaleRef.current + ZOOM_STATE_EPSILON);
				return;
			}
			if (pointerRecordsRef.current.size === 0) {
				gestureRef.current = {mode: 'idle'};
				updateDragging(false);
				// A pinch let go at fit has nothing left to animate.
				if (animationControlsRef.current.length === 0) rebuildLayerAfterZoom();
			}
		},
		[
			clearSingleTapTimer,
			clickToToggleZoom,
			disabledRef,
			getViewportRelativePoint,
			glideAfterPan,
			minScaleRef,
			onBackdropTapRef,
			onTapRef,
			onSwipeDownDismissRef,
			onSwipeNextRef,
			onSwipePreviousRef,
			panDisabledRef,
			prefersReducedMotion,
			rebuildLayerAfterZoom,
			scale,
			setTransform,
			stopAnimations,
			tapToToggleZoom,
			doubleTapToToggleZoom,
			updateDragging,
			x,
			y,
			zoomTo,
		],
	);
	const handlePointerCancel = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const shouldResetSwipe =
				gestureRef.current.mode === 'pan' && (gestureRef.current.swipeDownActive || gestureRef.current.swipeSideActive);
			pointerRecordsRef.current.delete(event.pointerId);
			gestureRef.current = {mode: 'idle'};
			updateDragging(false);
			if (shouldResetSwipe) {
				setTransform({scale: minScaleRef.current, x: 0, y: 0}, true);
			}
		},
		[minScaleRef, setTransform, updateDragging],
	);
	const handleDoubleClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			// Clicks already toggle the zoom; a double click would just undo and redo it.
			if (disabledRef.current || !doubleClickEnabled || clickToToggleZoom) return;
			if (!contentRef.current?.contains(event.target as Node)) return;
			event.preventDefault();
			const origin = getViewportRelativePoint(event.clientX, event.clientY) ?? {x: 0, y: 0};
			zoomTo(lastCommittedZoomStateRef.current === 'fit' ? 'zoomed' : 'fit', origin);
		},
		[clickToToggleZoom, disabledRef, doubleClickEnabled, getViewportRelativePoint, zoomTo],
	);
	const cursor = useMemo(() => {
		if (disabled) return 'default';
		if (!isHoveringContent) return 'default';
		if (tapToToggleZoom) return activeZoomState === 'zoomed' ? 'zoom-out' : 'zoom-in';
		if (isDragging) return 'grabbing';
		if (activeZoomState === 'zoomed') return 'grab';
		return doubleClickEnabled || clickToToggleZoom ? 'zoom-in' : 'default';
	}, [
		activeZoomState,
		clickToToggleZoom,
		disabled,
		doubleClickEnabled,
		isDragging,
		isHoveringContent,
		tapToToggleZoom,
	]);
	return {
		x,
		y,
		scale,
		zoomState: activeZoomState,
		isDragging,
		isHoveringContent,
		cursor,
		viewportBindings: {
			ref: viewportRef,
			onPointerDown: handlePointerDown,
			onPointerMove: handlePointerMove,
			onPointerUp: finishPointer,
			onPointerCancel: handlePointerCancel,
			onDoubleClick: handleDoubleClick,
		},
		contentKey,
		contentBindings: {
			ref: contentRef,
			onPointerEnter: () => setIsHoveringContent(true),
			onPointerLeave: () => setIsHoveringContent(false),
		},
		zoomTo,
		zoomIn,
		zoomOut,
		toggleActualSize,
		panBy,
		reset,
		getSnapshot,
	};
}
